import { Col } from 'components/common/Grid';

import { useCallback, useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill-new';
import { PromptWithBeforeUnload } from 'components/Form/PromptWithBeforeUnload';
import { ConfirmDialog } from 'components/Popup/ConfirmDialog/ConfirmDialog';
import WYSIWYG from 'components/WYSIWYG';
import { ReadOnlyQuill } from 'components/WYSIWYG/ReadOnlyQuill';
import { QuillPreview } from 'components/WYSIWYG/WYSIWYG.styled';
import { Card } from 'components/common';
import { WrappingRow } from 'components/common/BasicStyles.styled';
import { CommonButton } from 'components/common/Forms/Button';
import CheckboxInput from 'components/common/Forms/CheckboxInput';
import {
  CheckBoxesWrapper,
  FormActionsStyle,
} from 'components/common/Forms/Forms.styled';
import { CardLoader, Loading } from 'components/common/Loading';
import { SITE_ATTRIBUTE_GUIDELINES_PLACEMENT } from 'configs/siteAndTrustAttributes';
import { useCommonTranslation } from 'hooks/i18n/useCommonTranslation';
import { useSpecificSiteStringAttributes } from 'hooks/useSpecificSiteAttribute';
import {
  useGuidelinesTemplate,
  useMutateUpdateGuidelinesTemplate,
} from 'query/storage';
import { SiteData } from 'types/sites';
import {
  addOrReplaceGuidelinesPlacement,
  toGuidelinesPlacement,
} from 'utils/siteUtils';
import { Subject, useSubjectListener } from 'utils/subject';
import {
  getWysiwygHtml,
  isEmptyWysiwygHtml,
} from 'components/WYSIWYG/wysiwyg.utils';
import { ISitePreferencesProps } from 'containers/common/site/SitePreferences';
import { SiteGuidelinesError } from './SiteGuidelines.styled';
import {
  GUIDELINES_SELECTION_NONE,
  GUIDELINES_SELECTIONS,
  GuidelinesSelection,
  guidelinesSelectionLabelKeys,
} from './SiteGuidelinesUtils';

const PLACEMENT_RADIO_NAME = 'guidelinesPlacementSelection';

type Props = ISitePreferencesProps & {
  site: SiteData;
  saveEvent: Subject<void>;
};

export const SiteGuidelines = ({
  site,
  saveEvent,
  submitHandler,
  isFetching,
}: Props) => {
  const { t } = useCommonTranslation();

  const [isPreviewOn, setIsPreviewOn] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isTextMissing, setIsTextMissing] = useState(false);
  const { isLoading: isGuidelinesLoading, data } = useGuidelinesTemplate();
  const { mutateAsync: update, isLoading: isGuidelinesUpdating } =
    useMutateUpdateGuidelinesTemplate();
  const isLoading = isGuidelinesLoading || isGuidelinesUpdating || isFetching;
  const quillRef = useRef<ReactQuill>();
  const quillValue = quillRef.current?.value;

  const [savedPlacement] = useSpecificSiteStringAttributes(
    site,
    SITE_ATTRIBUTE_GUIDELINES_PLACEMENT,
  );
  const savedSelection: GuidelinesSelection = isEmptyWysiwygHtml(data?.data)
    ? GUIDELINES_SELECTION_NONE
    : toGuidelinesPlacement(savedPlacement);
  const [pickedSelection, setPickedSelection] = useState<GuidelinesSelection>();
  const selection = pickedSelection ?? savedSelection;

  useEffect(() => {
    setPickedSelection(undefined);
    setIsDirty(false);
    setIsTextMissing(false);
  }, [site.id]);

  const handleSelect = useCallback((value: GuidelinesSelection) => {
    setPickedSelection(value);
    setIsDirty(true);
    setIsTextMissing(false);
  }, []);

  const onSaveClick = useCallback(async () => {
    const quill = quillRef.current;
    if (!quill) return;
    if (selection === GUIDELINES_SELECTION_NONE) {
      await update('');
      quill.getEditor()?.setText('');
      setIsTextMissing(false);
      setIsDirty(false);
      return;
    }
    const html = getWysiwygHtml(quill);
    if (!html) {
      setIsTextMissing(true);
      return;
    }
    setIsTextMissing(false);
    await update(html);
    submitHandler(addOrReplaceGuidelinesPlacement(site, selection));
    setIsDirty(false);
  }, [selection, update, submitHandler, site]);

  useEffect(() => {
    const quill = quillRef.current?.editor;
    if (quill) {
      const handler = () => {
        setIsDirty(true);
        setIsTextMissing(false);
      };
      quill.on('text-change', handler);
      return () => {
        quill.off('text-change', handler);
      };
    }
  }, [quillRef.current?.editor]);

  useSubjectListener(
    saveEvent,
    () => {
      if (!isLoading) {
        onSaveClick();
      }
    },
    [isLoading, onSaveClick],
  );

  if (!data) {
    return isGuidelinesLoading ? (
      <WrappingRow style={{ position: 'relative', width: '100%' }}>
        <Loading style={{ marginTop: 'var(--s4)' }} />
      </WrappingRow>
    ) : null;
  }

  const hasGuidelines = selection !== GUIDELINES_SELECTION_NONE;

  return (
    <Col>
      <Card>
        <PromptWithBeforeUnload when={isDirty}></PromptWithBeforeUnload>
        <CheckBoxesWrapper isVertical>
          {GUIDELINES_SELECTIONS.map((value) => (
            <CheckboxInput
              type="radio"
              key={value}
              id={`${PLACEMENT_RADIO_NAME}-${value}`}
              name={PLACEMENT_RADIO_NAME}
              label={t(guidelinesSelectionLabelKeys[value])}
              value={value}
              checked={selection === value}
              onChange={() => handleSelect(value)}
            />
          ))}
        </CheckBoxesWrapper>
        {isTextMissing && (
          <SiteGuidelinesError hasError={true}>
            {t('guidelines-text-required')}
          </SiteGuidelinesError>
        )}
        <div style={{ display: hasGuidelines ? undefined : 'none' }}>
          <WYSIWYG ref={quillRef} defaultValue={data.data || ''} />
          <FormActionsStyle>
            <CommonButton
              variant="secondary"
              type="button"
              onClick={() => setIsPreviewOn(true)}
            >
              {t('preview')}
            </CommonButton>
          </FormActionsStyle>
        </div>
        {isPreviewOn && (
          <ConfirmDialog
            isLoading={false}
            handleClose={() => setIsPreviewOn(false)}
            description={
              <QuillPreview>
                <ReadOnlyQuill value={quillValue} />
              </QuillPreview>
            }
            status="success"
            actions={[
              <CommonButton
                variant="primary"
                onClick={() => setIsPreviewOn(false)}
                key="close-preview"
              >
                {t('close')}
              </CommonButton>,
            ]}
          />
        )}
        {isLoading && <CardLoader fillWrapper={true} />}
      </Card>
    </Col>
  );
};
